import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDashboard, useEvents, useNow, usePastEvents, usePublicData } from '../lib/hooks';
import { dateRangeText, eventEnded, fmtTime, KIND_LABEL, mealName, seats } from '../lib/format';
import { passVenues } from '../lib/live';
import type { Pass, PassVenue } from '../lib/types';
import { type EventPasses, groupByDay, groupByEvent, OrderRow, PassRow, PastEventCard, ResidentBar } from '../components/resident';
import { Spinner, VegMark } from '../components/ui';
import { FlatPassCard, FlatPassModal, PdfButton, type PassScope } from '../components/passes';
import { LiveStrip } from '../components/live';
import { AlertsCard } from '../components/alerts';
import { errorText } from '../lib/errors';
import { useAuth } from '../lib/auth';

const HOUR = 3600_000;
// Past events on My passes; the rest are on the Past events page.
const PAST_ON_HOME = 3;

function nowServing(passes: Pass[], now: number): Pass | undefined {
  return passes.find((p) => {
    if (p.state === 'void') return false;
    const open = new Date(p.sitting.opens_at).getTime() - 2 * HOUR;
    const close = new Date(p.sitting.closes_at).getTime() + p.sitting.grace_minutes * 60_000;
    return now >= open && now <= close;
  });
}

export default function Home() {
  const dash = useDashboard();
  const { me } = useAuth();
  const qc = useQueryClient();
  const { menu, venues, settings } = usePublicData();
  const ev = useEvents();
  const past = usePastEvents(PAST_ON_HOME, !!dash.data);
  const now = useNow();
  const [flatOpen, setFlatOpen] = useState(false);

  // The dashboard carries the events that are on: what the passes, the flat QR and the PDF are for. Past events are
  // cards from their own query. An event that closed since this phone saved the dashboard is left out until it reloads.
  const groups = groupByEvent((dash.data?.passes ?? []).filter((p) => p.state !== 'void'));
  const passes = groups.current.flatMap((g) => g.passes);
  const orders = (dash.data?.orders ?? []).filter((o) => !eventEnded(ev.byId.get(o.event_id ?? '')?.status));
  const pastItems = past.data?.items ?? [];
  const pastTotal = past.data?.total ?? 0;
  const current = nowServing(passes, now);
  const bookable = ev.live.find((e) => e.booking_open);
  const labelled = groups.current.length > 1 || pastTotal > 0;

  const flat = dash.data?.flat;
  const scope: PassScope | null = flat?.pass_token ? {
    flat: { label: flat.label, name: flat.contact_name, passToken: flat.pass_token },
    reissueHint: 'issue new QR codes under Menu → My family',
    onChanged: () => void qc.invalidateQueries({ queryKey: ['dashboard'] }),
  } : null;
  const confirmed = passes.some((p) => p.booked > 0);

  return (
    <div className="shell">
      <ResidentBar />
      <main className="app-body">
        {dash.isError && !dash.data && <p className="err">{errorText(dash.error)}</p>}
        {dash.isError && dash.data && <p className="fine">Showing your last saved passes. You seem to be offline.</p>}

        <LiveStrip cutoffs />
        {(me?.inbox ?? 0) > 0 && (
          <Link className="notice warn card-link" to="/account">
            <p><b>{me!.inbox === 1 ? 'Someone is waiting' : `${me!.inbox} requests are waiting`} for your answer</b></p>
            <p>A request to join your family, or to combine with another family at {me?.flat?.label}. Open My family ›</p>
          </Link>
        )}
        {current && (
          <NowCard pass={current} venues={passVenues(current, menu.data, venues.data)} now={now}
            lastCallMs={(settings.data?.last_call_minutes ?? 20) * 60_000} />
        )}
        <AlertsCard />
        {scope && confirmed && <FlatPassCard scope={scope} passes={passes} onOpen={() => setFlatOpen(true)} />}

        {dash.isPending && !dash.data ? (
          <div className="empty"><Spinner /></div>
        ) : orders.length === 0 ? (
          bookable ? (
            <div className="card empty">
              <p className="display" style={{ fontSize: 20 }}>{bookable.name}</p>
              <p>No meals booked yet. Choose your meals, then pay online, by UPI or in cash; your passes come here and by email.</p>
              <Link className="btn primary" to="/book">Book meals</Link>
            </div>
          ) : (
            <div className="card empty">
              <p className="display" style={{ fontSize: 20 }}>No event open for booking</p>
              <p>The Food Committee opens booking for each event. {pastTotal ? 'Your passes from past events are below.' : ''}</p>
            </div>
          )
        ) : (
          <>
            {groups.current.map((g) => (
              <EventDays key={g.id} group={g} title={labelled ? g.name || 'Meal passes' : 'Meal passes'} />
            ))}
            {passes.length === 0 && <p className="fine">Passes appear here once you book.</p>}
            {scope && confirmed && <PdfButton scope={scope} passes={passes} block />}
            <p className="app-h">Your orders</p>
            {orders.map((o) => <OrderRow key={o.code} order={o} />)}
            {bookable && <Link className="btn ghost block" to="/book">Book more meals</Link>}
          </>
        )}
        {ev.live.length > 0 && (
          <>
            <p className="app-h">What's on</p>
            {ev.live.map((e) => (
              <Link key={e.id} className="orow" to={`/e/${encodeURIComponent(e.id)}`}>
                <span className="orow-top"><b>{e.name}</b></span>
                <span className="orow-meta">
                  <span>{e.starts_on && e.ends_on ? dateRangeText(e.starts_on, e.ends_on) : ''}</span>
                  <span className="orow-go">Programme, contacts and notes ›</span>
                </span>
              </Link>
            ))}
          </>
        )}
        {pastItems.length > 0 && (
          <>
            <p className="app-h">Past events</p>
            {pastItems.map((e) => <PastEventCard key={e.id} event={e} back="/" />)}
            {pastTotal > pastItems.length && <Link className="btn ghost block" to="/history">All {pastTotal} past events</Link>}
          </>
        )}
      </main>
      {flatOpen && scope && <FlatPassModal scope={scope} passes={passes} onClose={() => setFlatOpen(false)} />}
    </div>
  );
}

function EventDays({ group, title }: { group: EventPasses; title: string }) {
  return (
    <>
      <p className="app-h">{title}</p>
      {groupByDay(group.passes).map((d) => (
        <section className="pday" key={d.key}>
          <p className="pday-h"><span className="bn">{d.bn}</span>{d.label}<span className="pday-date">{d.date}</span></p>
          {d.passes.map((p) => <PassRow key={p.id} pass={p} />)}
        </section>
      ))}
    </>
  );
}

/** Where to go for each kind with seats left: "2 adult · Veg → T6 Banquet". */
function WhereToGo({ pass, venues }: { pass: Pass; venues: PassVenue[] }) {
  const rows = pass.state === 'locked'
    ? pass.pending.map((p) => ({ kind: p.kind, a: p.adults, c: p.children }))
    : pass.lines.map((l) => ({ kind: l.kind, a: l.left_adults, c: l.left_children })).filter((r) => r.a + r.c > 0);
  if (!venues.length || !rows.length) return null;
  return (
    <ul className="now-where">
      {rows.map((r) => {
        const v = venues.find((x) => x.kind === r.kind);
        return (
          <li key={r.kind}>
            <span><VegMark kind={r.kind} />{KIND_LABEL[r.kind]} · {seats(r.a, r.c)}</span>
            {v && <Link to={`/venues#${v.id}`}>{v.name} ›</Link>}
          </li>
        );
      })}
    </ul>
  );
}

function NowCard({ pass, venues, now, lastCallMs }: { pass: Pass; venues: PassVenue[]; now: number; lastCallMs: number }) {
  const s = pass.sitting;
  const title = `${s.day_label} ${mealName(s)}`;
  const opens = new Date(s.opens_at).getTime();
  const closes = new Date(s.closes_at).getTime();
  const lastCall = now >= closes - lastCallMs && now < closes && pass.left > 0;
  const eyebrow = lastCall
    ? `Last call · closes at ${fmtTime(s.closes_at)}`
    : `${opens > now ? 'Opens' : 'Now serving'} · ${fmtTime(s.opens_at)}–${fmtTime(s.closes_at)}`;
  if (pass.state === 'locked') {
    return (
      <div className="now now-locked">
        <p className="now-eyebrow">{eyebrow}</p>
        <p className="now-title">{title}</p>
        <p className="now-text">
          Your pass unlocks when the Food Committee confirms {pass.blocking_order?.code ?? 'your payment'}. At the counter, the help desk can check it on the spot.
        </p>
        <WhereToGo pass={pass} venues={venues} />
        <Link className="btn ghost sm" to={`/pass/${s.id}`}>See pass</Link>
      </div>
    );
  }
  if (pass.state === 'fully_served' || pass.state === 'expired') {
    return (
      <div className="now">
        <p className="now-eyebrow">{eyebrow}</p>
        <p className="now-title">{title}</p>
        <p className="now-text">{pass.state === 'fully_served' ? `All ${pass.booked} seats served.` : 'This meal has closed.'}</p>
      </div>
    );
  }
  return (
    <div className={`now now-ready${lastCall ? ' now-last' : ''}`}>
      <p className="now-eyebrow">{eyebrow}</p>
      <p className="now-title">{title}</p>
      <p className="now-text">{lastCall ? `${pass.left} of ${pass.booked} seats still to use` : `${pass.left} of ${pass.booked} seats ready`}</p>
      <WhereToGo pass={pass} venues={venues} />
      <Link className="btn on-accent sm" to={`/pass/${s.id}`}>Show pass QR</Link>
    </div>
  );
}
